import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { MainLayoutContainer } from "components/Layout";
import Grid from "@mui/material/Grid2";
import { TableEvaluation } from "./components";
import { Link } from "react-router-dom";
import { FormikProvider, useFormik } from "formik";
import { FC, useEffect, useMemo, useState } from "react";
import { RelevantResourcesLink } from "./components/RelevantResourcesLink";
import {
  initialValuesCompetency,
  REQUIREMENT_COMPETENCY_TYPE,
  validationSchemaCompetency,
} from "./constants";
import { useRequirementCreateAndEdit } from "./useRequirementCreateAndEdit";
import { ANIMAL_SIZE, getLevelObject, REQUIREMENT_TYPE } from "../config";
import { EntitySelector } from "modals/EntitySelector";
import { Species } from "types/species";
import { REQUIREMENT } from "helpers/constants";
import { TagManager } from "components/TagManager";
import { BREADCRUMB_PAGE } from "components/Breadcrumb/constants";
import { EntitySelectorWithTabs } from "modals/EntitySelector";
import { useGetRequirementsByTypeQuery } from "graphql/__generated__/hooks";
import { Requirement } from "types/requirement";
import { EvaluationMetric } from "types/evaluationMetric";
import { Resource } from "types/resource";

type CompetencyCreateAndEditProps = {
  edit?: boolean;
};

export const CompetencyCreateAndEdit: FC<CompetencyCreateAndEditProps> = ({
  edit = false,
}) => {
  const {
    relevantResourceLinks,
    isOrgView,
    setRelevantResourceLinks,
    handleCreateRequirement,
    handleEditRequirement,
    selectedSpecies,
    setSelectedSpecies,
    selectSpeciesOpen,
    setSelectSpeciesOpen,
    species,
    dataRequirement,
    evaluationMetrics,
    setEvaluationMetrics,
    tags,
    handleAddTag,
    handleDeleteTag,
    customLabels,
    organizationName,
  } = useRequirementCreateAndEdit(REQUIREMENT.REQUIREMENT_COMPETENCY, edit);

  const [openImportRequirementModal, setOpenImportRequirementModal] =
    useState(false);

  const { data: requirementsData } = useGetRequirementsByTypeQuery({
    variables: { reqType: REQUIREMENT.REQUIREMENT_COMPETENCY },
  });

  const competencyRequirements = useMemo(
    () => (requirementsData?.requirement as Requirement[]) || [],
    [requirementsData],
  );

  const titleSuffix =
    isOrgView && organizationName ? ` [${organizationName}]` : "";

  const formik = useFormik({
    initialValues: initialValuesCompetency,
    validationSchema: validationSchemaCompetency,
    onSubmit: (values) => {
      if (edit) {
        handleEditRequirement(values).then();
      } else {
        handleCreateRequirement(values).then();
      }
    },
  });

  // FIll Formik values with requirement data to Edit
  useEffect(() => {
    if (dataRequirement?.requirement_by_pk && edit) {
      const [hour, minutes] =
        dataRequirement.requirement_by_pk.task_time?.split(":") || [];

      formik.setValues({
        name: dataRequirement.requirement_by_pk.name || "",
        overview: dataRequirement.requirement_by_pk.overview ?? "",
        description: dataRequirement.requirement_by_pk.description ?? "",
        requirement_type:
          (dataRequirement.requirement_by_pk
            .requirement_type as REQUIREMENT_COMPETENCY_TYPE) ?? "",
        animal_size: dataRequirement.requirement_by_pk.animal_size ?? "",

        min_performance: dataRequirement.requirement_by_pk.min_performance ?? 0,
        min_large_animals_count:
          dataRequirement.requirement_by_pk.min_large_animals_count ?? 0,
        min_small_animals_count:
          dataRequirement.requirement_by_pk.min_small_animals_count ?? 0,
        hour: hour ? parseInt(hour) : 0,
        minutes: minutes ? parseInt(minutes) : 0,
        min_score: dataRequirement.requirement_by_pk.min_score ?? 0,
      });
    }
  }, [dataRequirement, edit]);

  const isEPA = useMemo(
    () => formik.values.requirement_type === REQUIREMENT_COMPETENCY_TYPE.EPA,
    [formik],
  );

  return (
    <MainLayoutContainer
      title={`${edit ? "Edit" : "Create"} Competency${titleSuffix}`}
      breadcrumb={
        isOrgView
          ? BREADCRUMB_PAGE.ORGANIZATION_REQUIREMENT_COMPETENCIES
          : BREADCRUMB_PAGE.COMPETENCY_DETAIL
      }
    >
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ maxWidth: 960 }}>
            <Box mb={2}>
              {!edit && (
                <Box mb={2} display="flex" justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setOpenImportRequirementModal(true)}
                  >
                    Import from Existing Competency
                  </Button>
                </Box>
              )}

              <Typography variant="h5">Requirement Overview</Typography>
              <Divider />
            </Box>

            <Stack spacing={1}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Box>
                    <TextField
                      size="small"
                      id="name"
                      name="name"
                      label="Requirement Name"
                      fullWidth
                      margin="dense"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.name && !!formik.errors.name}
                      helperText={formik.touched.name && formik.errors.name}
                    />
                  </Box>
                </Grid>
              </Grid>

              <Box>
                <TextField
                  size="small"
                  id="overview"
                  name="overview"
                  label="Requirement Overview"
                  fullWidth
                  margin="dense"
                  multiline
                  rows={4}
                  value={formik.values.overview}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.overview && !!formik.errors.overview}
                  helperText={formik.touched.overview && formik.errors.overview}
                />
              </Box>
              <Box>
                <TextField
                  size="small"
                  id="description"
                  name="description"
                  label="Requirement Full Description"
                  fullWidth
                  margin="dense"
                  multiline
                  rows={5}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.description && !!formik.errors.description
                  }
                  helperText={
                    formik.touched.description && formik.errors.description
                  }
                />
              </Box>
            </Stack>

            <Box my={2}>
              <Typography variant="h5">Goal Definition</Typography>
              <Divider />
            </Box>

            <Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Stack spacing={2}>
                    <Box>
                      <FormControl fullWidth>
                        <InputLabel id="requirement_type">
                          Requirement Type
                        </InputLabel>
                        <Select
                          size="small"
                          id="requirement_type"
                          name="requirement_type"
                          label="Competency Type"
                          fullWidth
                          margin="dense"
                          value={formik.values.requirement_type}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.requirement_type &&
                            !!formik.errors.requirement_type
                          }
                        >
                          {REQUIREMENT_TYPE.map((reqType) => (
                            <MenuItem value={reqType.value} key={reqType.value}>
                              {reqType.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    {isEPA && (
                      <>
                        <Box>
                          <TextField
                            size="small"
                            id="min_small_animals_count"
                            name="min_small_animals_count"
                            label="Minimum Small Animal Count"
                            fullWidth
                            margin="dense"
                            type="number"
                            value={formik.values.min_small_animals_count}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                              formik.touched.min_small_animals_count &&
                              !!formik.errors.min_small_animals_count
                            }
                            helperText={
                              formik.touched.min_small_animals_count &&
                              formik.errors.min_small_animals_count
                            }
                          />
                        </Box>
                        <Box>
                          <TextField
                            size="small"
                            id="min_large_animals_count"
                            name="min_large_animals_count"
                            label="Minimum Large Animal Count"
                            fullWidth
                            margin="dense"
                            type="number"
                            value={formik.values.min_large_animals_count}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                              formik.touched.min_large_animals_count &&
                              !!formik.errors.min_large_animals_count
                            }
                            helperText={
                              formik.touched.min_large_animals_count &&
                              formik.errors.min_large_animals_count
                            }
                          />
                        </Box>
                      </>
                    )}

                    {!isEPA && (
                      <Box>
                        <Stack direction="row" spacing={2}>
                          <TextField
                            size="small"
                            id="species_id"
                            name="species_id"
                            label="Specific Species"
                            fullWidth
                            margin="dense"
                            type="text"
                            value={selectedSpecies?.name ?? "Any"}
                          />
                          <Button
                            variant="outlined"
                            onClick={() => setSelectSpeciesOpen(true)}
                          >
                            Select
                          </Button>
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Stack spacing={2}>
                    {isEPA && (
                      <Box>
                        <FormControl fullWidth>
                          <InputLabel id="animal_size">Animal Size</InputLabel>
                          <Select
                            size="small"
                            id="animal_size"
                            name="animal_size"
                            label="Animal Size"
                            fullWidth
                            margin="dense"
                            value={formik.values.animal_size}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                              formik.touched.animal_size &&
                              !!formik.errors.animal_size
                            }
                          >
                            {ANIMAL_SIZE.map((size) => (
                              <MenuItem value={size.value} key={size.value}>
                                {size.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    )}
                    {!isEPA && (
                      <Box>
                        <TextField
                          size="small"
                          id="min_performance"
                          name="min_performance"
                          label="Frequency requirement"
                          fullWidth
                          margin="dense"
                          type="number"
                          value={formik.values.min_performance}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.min_performance &&
                            !!formik.errors.min_performance
                          }
                          helperText={
                            formik.touched.min_performance &&
                            formik.errors.min_performance
                          }
                        />
                      </Box>
                    )}
                    <Box>
                      <Box>
                        <FormControl fullWidth>
                          <InputLabel id="min_score">
                            Level Requirement
                          </InputLabel>
                          <Select
                            size="small"
                            id="min_score"
                            name="min_score"
                            label="Level Requirement"
                            fullWidth
                            margin="dense"
                            value={formik.values.min_score}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                              formik.touched.min_score &&
                              !!formik.errors.min_score
                            }
                          >
                            {getLevelObject(formik.values.requirement_type).map(
                              (reqType) => (
                                <MenuItem
                                  value={reqType.value}
                                  key={reqType.value}
                                >
                                  {reqType.label}
                                </MenuItem>
                              ),
                            )}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>
                    <Stack direction={{ sm: "row", xs: "column" }} spacing={2}>
                      <Box sx={{ minWidth: 135, height: 3, lineHeight: 3 }}>
                        Allowed Task Time
                      </Box>
                      <Box sx={{ paddingTop: { xs: 4, sm: 0 } }}>
                        <Stack direction="row" spacing={2}>
                          <TextField
                            size="small"
                            id="hour"
                            name="hour"
                            label="Hour"
                            margin="dense"
                            type="number"
                            value={formik.values.hour}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.hour && !!formik.errors.hour}
                            helperText={
                              formik.touched.hour && formik.errors.hour
                            }
                          />
                          <TextField
                            size="small"
                            id="minutes"
                            name="minutes"
                            label="Minutes"
                            margin="dense"
                            type="number"
                            value={formik.values.minutes}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                              formik.touched.minutes && !!formik.errors.minutes
                            }
                            helperText={
                              formik.touched.minutes && formik.errors.minutes
                            }
                          />
                        </Stack>
                      </Box>
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            <Box my={2}>
              <Typography variant="h5">Evaluation Rubric</Typography>
              <Divider />
            </Box>

            <Box mt={2}>
              <TableEvaluation
                requirementType={REQUIREMENT.REQUIREMENT_COMPETENCY}
                evaluationMetrics={evaluationMetrics}
                setEvaluationMetrics={setEvaluationMetrics}
                customLabels={customLabels}
              />
            </Box>

            <Box my={2}>
              <Typography variant="h5">Supplemental Information</Typography>
              <Divider />
            </Box>

            <Box>
              <RelevantResourcesLink
                relevantResourceLinks={relevantResourceLinks}
                onChange={(newRelevantResourceLinks) =>
                  setRelevantResourceLinks(newRelevantResourceLinks)
                }
              />
            </Box>

            <Box my={2}>
              <Typography variant="h5">Tags</Typography>
              <Divider />
            </Box>

            <Box>
              <TagManager
                tags={tags}
                onAdd={handleAddTag}
                onDelete={handleDeleteTag}
                isEditing={true}
              />
            </Box>

            <Box mt={2}>
              <Stack direction="row-reverse" spacing={2}>
                <Button variant="contained" type="submit">
                  Save Changes
                </Button>
                <Button variant="outlined" component={Link} to="/competencies">
                  Cancel
                </Button>
              </Stack>
            </Box>
          </Box>
        </form>
      </FormikProvider>

      {selectSpeciesOpen && (
        <EntitySelector
          open={selectSpeciesOpen}
          onClose={() => {
            setSelectSpeciesOpen(false);
          }}
          title="Select Species"
          filterCriteria={(search, data) =>
            data.name.toLowerCase().includes(search.toLowerCase())
          }
          listEntities={species}
          onSelect={(selected: Species) => {
            setSelectedSpecies(selected);
          }}
          keyField="species_id"
          enableNull
        />
      )}

      {openImportRequirementModal && (
        <EntitySelectorWithTabs
          open={openImportRequirementModal}
          onClose={() => setOpenImportRequirementModal(false)}
          title="Import from Existing Competency"
          listEntities={competencyRequirements}
          filterCriteria={(search: string, data: Requirement) =>
            data.name.toLowerCase().includes(search.toLowerCase())
          }
          onSelect={(selected: Requirement | null) => {
            if (!selected) return;

            const [hour, minutes] = selected.task_time?.split(":") || [];
            const importedName =
              organizationName && isOrgView
                ? `${selected.name} [${organizationName}]`
                : selected.name || "";

            formik.setValues({
              ...formik.values,
              name: importedName,
              overview: selected.overview ?? "",
              description: selected.description ?? "",
              requirement_type:
                (selected.requirement_type as REQUIREMENT_COMPETENCY_TYPE) || "",
              animal_size: selected.animal_size ?? "",
              min_performance: selected.min_performance ?? 0,
              min_large_animals_count:
                selected.min_large_animals_count ?? 0,
              min_small_animals_count:
                selected.min_small_animals_count ?? 0,
              hour: hour ? parseInt(hour, 10) : 0,
              minutes: minutes ? parseInt(minutes, 10) : 0,
              min_score: selected.min_score ?? 0,
            });

            if (selected.species) {
              setSelectedSpecies(selected.species as Species);
            } else {
              setSelectedSpecies(null);
            }

            setEvaluationMetrics(
              (selected.evaluation_metrics as EvaluationMetric[]) || [],
            );

            setRelevantResourceLinks(
              (selected.resources || []).map(
                (r: any) => r.resource as Resource,
              ),
            );
          }}
          keyField="requirement_id"
          displayField="name"
          tabs={[
            {
              label: "Competency",
              filterTabCriteria: (requirement: Requirement) =>
                requirement?.type === REQUIREMENT.REQUIREMENT_COMPETENCY,
            },
          ]}
        />
      )}
    </MainLayoutContainer>
  );
};
